import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass, ArrowRight, Download } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, API_BASE } from "@/lib/api";

const STATUSES = [
  "all", "submitted", "under_review", "clarification_required",
  "discovery_call_required", "proposal_in_progress", "won", "lost", "closed",
];

export default function IdeaLabAdmin() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (status !== "all") params.status = status;
    if (q) params.q = q;
    api.get("/idealab/admin/submissions", { params })
      .then(({ data }) => setItems(data || []))
      .finally(() => setLoading(false));
  }, [status, q]);

  const stats = useMemo(() => {
    const total = items.length;
    const byStatus = items.reduce((acc, x) => { acc[x.status] = (acc[x.status] || 0) + 1; return acc; }, {});
    return { total, byStatus };
  }, [items]);

  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = {};
      if (status !== "all") params.status = status;
      const res = await api.get("/idealab/admin/submissions.csv", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `appistry-idealab-submissions-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not export CSV. Try again.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-8 md:p-10" data-testid="admin-idealab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="overline mb-2">IdeaLab</p>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Discovery submissions</h1>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={exportCsv}
            disabled={exporting || items.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-mono uppercase tracking-widest border border-zinc-200 hover:border-zinc-400 disabled:opacity-40"
            data-testid="idealab-export-csv"
            title={items.length === 0 ? "Nothing to export yet" : "Export the current filter as CSV"}
          >
            <Download size={14} weight="bold" /> {exporting ? "Exporting…" : "Export CSV"}
          </button>
          <span className="text-xs font-mono uppercase tracking-widest text-zinc-500" data-testid="idealab-count">{stats.total} records</span>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 mb-4 p-4 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2 border border-zinc-200 px-3 py-2 flex-1 min-w-[240px]">
          <MagnifyingGlass size={16} className="text-zinc-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search reference, name, email, organisation…"
            className="flex-1 outline-none text-sm bg-transparent"
            data-testid="idealab-search"
          />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-zinc-200 px-3 py-2 text-sm bg-white" data-testid="idealab-status-filter">
          {STATUSES.map((s) => <option key={s} value={s}>{s === "all" ? "All statuses" : s.replace(/_/g, " ")}</option>)}
        </select>
      </div>

      <div className="bg-white border border-zinc-200 overflow-x-auto" data-testid="idealab-table">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50">
            <tr className="text-left">
              <Th>Reference</Th>
              <Th>Name / Organisation</Th>
              <Th>Email</Th>
              <Th>Readiness</Th>
              <Th>Status</Th>
              <Th>Submitted</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-500 font-mono">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-500 font-mono">No submissions yet.</td></tr>
            ) : (
              items.map((s) => (
                <tr key={s.reference_number} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`idealab-row-${s.reference_number}`}>
                  <td className="px-4 py-3 font-mono text-xs">{s.reference_number}</td>
                  <td className="px-4 py-3">
                    <div className="font-semibold">{s.contact?.full_name || "—"}</div>
                    <div className="text-xs text-zinc-500">{s.contact?.organisation || "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-zinc-700">{s.contact?.email}</td>
                  <td className="px-4 py-3"><ReadinessPill v={s.lead_assessment?.lead_readiness} /></td>
                  <td className="px-4 py-3"><StatusPill v={s.status} /></td>
                  <td className="px-4 py-3 text-zinc-500 text-xs">{formatDate(s.submitted_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/admin/idealab/${s.reference_number}`} className="text-sm font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all" data-testid={`idealab-open-${s.reference_number}`}>
                      Open <ArrowRight size={12} weight="bold" />
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children }) {
  return <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500">{children}</th>;
}

function ReadinessPill({ v }) {
  if (!v) return <span className="text-xs text-zinc-400">—</span>;
  const c = v === "High" ? "bg-emerald-100 text-emerald-700" : v === "Medium" ? "bg-amber-100 text-amber-700" : "bg-zinc-100 text-zinc-600";
  return <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 ${c}`}>{v}</span>;
}

function StatusPill({ v }) {
  return <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 bg-zinc-900 text-white">{(v || "submitted").replace(/_/g, " ")}</span>;
}

function formatDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(); } catch { return s; }
}
